import {Offer} from '../getDiscountOffer';
import {useEffect, useState} from 'react';
import axios from 'axios';
import Environment from '../Environment';
import Analytics from '../Analytics';
import {useSearchParams} from '../urlQuery';
import {getLinkOutParam, getLinkOutSession} from '../linkOutSession';

/**
 * Hypnos API base.
 *
 * Defaults to production and is only overridable by setting `REACT_APP_API_BASE_URL`, which
 * replaces the pair of commented-out constants this used to be ("figure out better way to
 * switch for testing" — this is it). That pattern meant pointing the app at a local Hypnos
 * required editing a tracked file, so the localhost value was one forgotten `git add` away
 * from shipping, and every Netlify context silently depended on nobody having done that.
 *
 * Defaulting to production inverts the failure: an unset variable talks to the real backend
 * instead of to a host that doesn't exist. Point it somewhere else in `.env.development`:
 *
 *   REACT_APP_API_BASE_URL=http://localhost:8000
 *
 * Note that Stripe mode is a *separate* axis, controlled by `REACT_APP_ENV` +
 * `REACT_APP_STRIPE_KEY`. Running against production Hypnos with `REACT_APP_ENV=test` is a
 * deliberate and supported combination: real accounts, promotions and entitlements, test-mode
 * money. Endpoints that take an `env` field honour it.
 */
export const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.risesci.com';

export function useOffer(code: string): Offer | null {
  const [offer, setOffer] = useState(null);
  const shouldClearCache = useClearCacheFromParams();

  const getNewOffer = async (code: string) => {
    const data = {
      code,
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      clear_cache: shouldClearCache,
    };

    const response = await axios.post(`${BASE_URL}/api/stripe-offer`, data);

    if (response.status !== 200) {
      Analytics.track('Could not fetch offer', {
        code,
        env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      });
      throw new Error(`Status: ${response.status} ${response.statusText}`);
    }
    return response.data;
  };

  useEffect(() => {
    getNewOffer(code).then(result => setOffer(result));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return offer;
}

export function useValueParams(value: string): string | null {
  const params = useSearchParams();
  return params.get(value) ?? getLinkOutParam(value);
}

export function useInviteIdFromParams(): string | null {
  const params = useSearchParams();
  return params.get('invite_id');
}

export function useClearCacheFromParams(): boolean {
  const params = useSearchParams();
  return Boolean(params.get('clear_cache'));
}

export function useQueryToken() {
  const params = useSearchParams();
  if (params.get('token')) {
    const token = decodeURIComponent(params.get('token') as string);
    return token.replace(' ', '+');
  }
  return params.get('user_id') ?? getLinkOutSession()?.personId ?? null;
}

export function useEmail() {
  const params = useSearchParams();
  if (params.get('email')) {
    const token = decodeURIComponent(params.get('email') as string);
    return token.replace(' ', '+');
  }
  return '';
}

export function useCode() {
  const params = useSearchParams();
  if (params.get('code')) {
    return decodeURIComponent(params.get('code') as string);
  }
  return '';
}

export function abbreviateFixedDiscount(offer: Offer): string {
  if (offer.fixedDiscount) {
    const lastTwoDigits = offer.fixedDiscount.slice(-2);
    if (lastTwoDigits === '00') {
      return offer.fixedDiscount.slice(0, -3);
    }
    return offer.fixedDiscount;
  }
  return '';
}
